/**
 * Seven-step enterprise transformation journey (display order).
 * Mounted under `EA_CANVAS_BASE` (e.g. `/ea-canvas`).
 * Enterprise Blueprint is the canonical “Architecture” view; `/architecture` redirects there.
 */

export const EA_CANVAS_BASE = "/ea-canvas";

export const GUIDED_FLOW_STEPS = [
  { step: 1, title: "Strategy canvas", shortLabel: "1. Blueprint" },
  { step: 2, title: "Configuration", shortLabel: "2. Config" },
  { step: 3, title: "Review structure", shortLabel: "3. Structure" },
  { step: 4, title: "Analyze findings", shortLabel: "4. Findings" },
  { step: 5, title: "Plan roadmap", shortLabel: "5. Roadmap" },
  { step: 6, title: "Governance", shortLabel: "6. Gov" },
  { step: 7, title: "Reports", shortLabel: "7. Reports" },
] as const;

export type GuidedFlowStepNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Path inside the EA canvas module (leading slash, no base prefix). */
export function pathWithinEaCanvas(pathname: string): string {
  const p = pathname.replace(/\/$/, "") || "/";
  const base = EA_CANVAS_BASE.replace(/\/$/, "");
  if (p === base || p === `${base}/`) return "/";
  if (p.startsWith(`${base}/`)) {
    const rest = p.slice(base.length);
    return rest && rest.length > 0 ? rest : "/";
  }
  return p;
}

/** Current step from pathname; `null` = home or non-flow page. */
export function getCurrentFlowStep(pathname: string): GuidedFlowStepNumber | null {
  const path = pathWithinEaCanvas(pathname).replace(/\/$/, "") || "/";
  if (path === "/" || path === "") return null;
  if (path === "/enterprise-blueprint" || path === "/architecture") return 1;
  if (path === "/configuration" || path === "/data-management") return 2;
  if (path === "/anatomy" || path === "/heatmap" || path === "/app-matrix") return 3;
  if (path === "/findings") return 4;
  if (path === "/roadmap") return 5;
  if (path === "/governance") return 6;
  if (path === "/reports") return 7;
  return null;
}

export interface NextStepConfig {
  to: string;
  label: string;
}

/** Linear next action per route (inner paths, no `EA_CANVAS_BASE`). */
export const NEXT_STEP_BY_PATH: Record<string, NextStepConfig> = {
  "/": { to: "/enterprise-blueprint", label: "Next: Architecture" },
  "/enterprise-blueprint": { to: "/configuration", label: "Next: Configuration" },
  "/configuration": { to: "/anatomy", label: "Next: Enterprise anatomy" },
  "/data-management": { to: "/anatomy", label: "Next: Enterprise anatomy" },
  "/anatomy": { to: "/heatmap", label: "Next: Capability heatmap" },
  "/heatmap": { to: "/app-matrix", label: "Next: App contribution" },
  "/app-matrix": { to: "/findings", label: "Next: Key findings" },
  "/findings": { to: "/roadmap", label: "Next: Roadmap" },
  "/roadmap": { to: "/governance", label: "Next: Governance" },
  "/governance": { to: "/reports", label: "Next: Reports" },
  "/reports": { to: "/", label: "Back to dashboard" },
};

function withBase(innerTo: string): string {
  if (innerTo === "/") return EA_CANVAS_BASE;
  return `${EA_CANVAS_BASE}${innerTo.startsWith("/") ? innerTo : `/${innerTo}`}`;
}

export function getNextStepForPath(pathname: string): NextStepConfig | null {
  const inner = pathWithinEaCanvas(pathname).replace(/\/$/, "") || "/";
  const step = NEXT_STEP_BY_PATH[inner];
  if (!step) return null;
  return { ...step, to: withBase(step.to === "/" ? "/" : step.to) };
}

export function isStepCompleted(current: GuidedFlowStepNumber | null, step: GuidedFlowStepNumber): boolean {
  if (current === null) return false;
  return current > step;
}

export function isStepCurrent(current: GuidedFlowStepNumber | null, step: GuidedFlowStepNumber): boolean {
  return current === step;
}
